import type { MathPracticeSheet } from '../content/math-library';
import type { SmAnswer } from '../content/schemas';
import type { Sheet } from '../content/workbook';
import { generatedItems, libraryItems, type MathItem, sumItems } from './math';

/**
 * Quick math is measured by time, not by sheets: a sheet that runs out before the day's
 * target goes on to the next one (past the last, the first again) when the person keeps
 * going. These are the sheets a session can work through, in order, and each one's questions.
 */
export interface MathSheets {
  ids: readonly string[];
  items(id: string): MathItem[];
}

function byLineage<T extends { lineageUuid: string }>(
  sheets: readonly T[],
  items: (sheet: T) => MathItem[],
): MathSheets {
  return {
    ids: sheets.map((sheet) => sheet.lineageUuid),
    items: (id) => {
      const sheet = sheets.find((candidate) => candidate.lineageUuid === id);
      return sheet ? items(sheet) : [];
    },
  };
}

/** A Math library row's sheets. */
export function librarySheets(sheets: readonly MathPracticeSheet[]): MathSheets {
  return byLineage(sheets, libraryItems);
}

/** The workbook's sheets of sums. */
export function workbookSheets(sheets: readonly Sheet[]): MathSheets {
  return byLineage(sheets, (sheet) => sumItems(sheet.problems));
}

/** Without a workbook: a week of short generated sessions from `seed` (the day) on. */
export function generatedSheets(seed: number): MathSheets {
  return {
    ids: Array.from({ length: 7 }, (_, day) => String(seed + day)),
    items: (id) => generatedItems(Number(id)),
  };
}

/** The sheet after `id`: the next one, past the last the first again. */
export function sheetAfter(sheets: MathSheets, id: string): string {
  const at = sheets.ids.indexOf(id);
  return sheets.ids[(at + 1) % sheets.ids.length] ?? id;
}

/** The questions of the sheets `run` went through, one sheet after the other. */
export function runItems(sheets: MathSheets, run: readonly string[]): MathItem[] {
  return run.flatMap((id) => sheets.items(id));
}

/**
 * The sheets of `run` the person worked on, once each, in order: up to the sheet of the last
 * answer — a sheet moved on to and left unanswered doesn't count. At least the first.
 */
export function usedSheets(
  sheets: MathSheets,
  run: readonly string[],
  answers: readonly SmAnswer[],
): string[] {
  const last = Math.max(-1, ...answers.map((answer) => answer.index));
  const used: string[] = [];
  let start = 0;
  for (const id of run) {
    if (used.length > 0 && start > last) break;
    if (!used.includes(id)) used.push(id);
    start += sheets.items(id).length;
  }
  return used;
}

/**
 * Where Quick math left earlier today picks up. Begun on `start`, the same sheet: the sheets
 * it went through, the next question and the answers so far — past the last question, that
 * one is asked again and its earlier answer dropped. Begun on any other sheet (another row
 * chosen in the Library, say), from the first question of `start`.
 */
export function resumeRun(
  sheets: MathSheets,
  start: string,
  left: { index: number; answers: SmAnswer[]; sheets?: string[] } | undefined,
): { run: string[]; index: number; answers: SmAnswer[] } {
  const run = left?.sheets;
  if (!left || !run || run[0] !== start || !run.every((id) => sheets.ids.includes(id))) {
    return { run: [start], index: 0, answers: [] };
  }
  const items = runItems(sheets, run);
  // A sheet whose questions changed since (the library regrouped) starts over.
  if (left.answers.some((answer) => !asks(items[answer.index], answer))) {
    return { run: [start], index: 0, answers: [] };
  }
  const index = Math.max(0, Math.min(left.index, items.length - 1));
  return { run, index, answers: left.answers.filter((answer) => answer.index < index) };
}

/** Whether `answer` answered the question `item` asks. */
function asks(item: MathItem | undefined, answer: SmAnswer): boolean {
  if (!item) return false;
  if ('sum' in item.from) {
    const { a, op, b } = item.from.sum;
    return 'a' in answer && answer.a === a && answer.op === op && answer.b === b;
  }
  return (
    'problem' in answer &&
    answer.step === item.from.step &&
    canonical(answer.problem) === canonical(item.from.problem)
  );
}

/** JSON with every object's keys sorted: a symbol puzzle's `values` keep no fixed order. */
const canonical = (value: unknown) =>
  JSON.stringify(value, (_, part) =>
    part && typeof part === 'object' && !Array.isArray(part)
      ? Object.fromEntries(Object.entries(part).sort(([a], [b]) => (a < b ? -1 : 1)))
      : part,
  );
